import {
  createNestablePublicClientApplication,
  InteractionRequiredAuthError,
  IPublicClientApplication,
} from "@azure/msal-browser";

const CLIENT_ID = "52696375-3866-44be-a19e-f0eeea9b248c";
// User.Read est requis pour GET /me (vérification d'identité côté serveur) --
// sans lui, Microsoft Graph répond 403 sur /me même avec un token Mail.Read valide.
const SCOPES = ["Mail.Read", "User.Read"];

let msalInstance: IPublicClientApplication | undefined;

async function getMsalInstance(): Promise<IPublicClientApplication> {
  if (!msalInstance) {
    msalInstance = await createNestablePublicClientApplication({
      auth: {
        clientId: CLIENT_ID,
        authority: "https://login.microsoftonline.com/common",
      },
      cache: {
        cacheLocation: "localStorage",
      },
    });
  }
  return msalInstance;
}

export async function acquireTokenSilent(): Promise<string> {
  const client = await getMsalInstance();
  const result = await client.acquireTokenSilent({ scopes: SCOPES });
  return result.accessToken;
}

export async function acquireTokenInteractive(): Promise<string> {
  const client = await getMsalInstance();
  const result = await client.acquireTokenPopup({ scopes: SCOPES });
  return result.accessToken;
}

export function isInteractionRequired(error: unknown): boolean {
  return error instanceof InteractionRequiredAuthError;
}

export async function acquireGraphToken(): Promise<string | undefined> {
  try {
    return await acquireTokenSilent();
  } catch (error) {
    if (!isInteractionRequired(error)) {
      console.error("Échec de l'acquisition silencieuse du token Graph", error);
      return undefined;
    }
  }

  try {
    return await acquireTokenInteractive();
  } catch (error) {
    console.error("Échec de l'acquisition interactive du token Graph", error);
    return undefined;
  }
}

export async function getAccountEmail(): Promise<string | undefined> {
  const client = await getMsalInstance();
  const account = client.getActiveAccount() ?? client.getAllAccounts()[0];
  return account?.username;
}

// clearCache()/logoutRedirect()/logoutPopup() lèvent toutes "unsupported_method" en mode Nested
// App Auth (le mode utilisé par ce taskpane dans Outlook) -- NAA délègue l'identité à l'appli hôte
// et n'expose aucune API supportée pour vider son propre cache (voir NestedAppAuthController dans
// @azure/msal-browser). On tente quand même clearCache() au cas où (mode non-NAA, futur SDK), mais
// le vidage réel passe par localStorage directement, seul moyen fiable ici. Un vrai changement de
// compte Microsoft nécessite en plus de vider les cookies login.microsoftonline.com/live.com du
// navigateur, hors de portée de ce module.
export async function logout(): Promise<void> {
  try {
    const client = await getMsalInstance();
    await client.clearCache();
  } catch {
    // Unsupported en mode NAA -- on se rabat sur le vidage direct de localStorage ci-dessous.
  }
  localStorage.clear();
}

async function fetchGraphWithRetry(url: string, accessToken: string, headers: Record<string, string> = {}, maxRetries = 3): Promise<Response> {
  for (let attempt = 0; ; attempt++) {
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        ...headers,
      },
    });

    if (response.status !== 429 || attempt >= maxRetries) {
      return response;
    }

    const retryAfterSeconds = Number(response.headers.get("Retry-After"));
    const delayMs = (Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0 ? retryAfterSeconds : 2 ** attempt) * 1000;
    await new Promise((resolve) => setTimeout(resolve, delayMs));
  }
}

export interface ConversationMessage {
  id: string;
  subject: string;
  from: string;
  receivedDateTime: string;
  bodyText: string;
}

let deletedItemsFolderIdCache: string | undefined;

// /me/messages n'est pas scopé à un dossier par défaut : il remonte aussi les messages
// des Éléments supprimés (tant qu'ils ne sont pas purgés). On récupère l'id de ce dossier
// pour les exclure explicitement du thread côté client.
async function getDeletedItemsFolderId(accessToken: string): Promise<string | undefined> {
  if (deletedItemsFolderIdCache) {
    return deletedItemsFolderIdCache;
  }
  try {
    const response = await fetchGraphWithRetry(
      "https://graph.microsoft.com/v1.0/me/mailFolders/deleteditems?$select=id",
      accessToken
    );
    if (!response.ok) {
      return undefined;
    }
    const data = await response.json();
    deletedItemsFolderIdCache = data.id;
    return deletedItemsFolderIdCache;
  } catch {
    return undefined;
  }
}

export async function getConversationMessages(
  conversationId: string,
  accessToken: string
): Promise<ConversationMessage[]> {
  const filter = encodeURIComponent(`conversationId eq '${conversationId}'`);
  const select = "id,subject,from,receivedDateTime,body,parentFolderId";
  const url = `https://graph.microsoft.com/v1.0/me/messages?$filter=${filter}&$select=${select}`;

  const [response, deletedItemsFolderId] = await Promise.all([
    fetchGraphWithRetry(url, accessToken, { Prefer: 'outlook.body-content-type="text"' }),
    getDeletedItemsFolderId(accessToken),
  ]);

  if (!response.ok) {
    throw new Error(`Graph request failed: ${response.status}`);
  }

  const data = await response.json();

  const messages: ConversationMessage[] = (data.value || [])
    .filter(
      (message: { parentFolderId?: string }) =>
        !deletedItemsFolderId || message.parentFolderId !== deletedItemsFolderId
    )
    .map(
      (message: {
        id: string;
        subject?: string;
        from?: { emailAddress?: { name?: string; address?: string } };
        receivedDateTime: string;
        body?: { content?: string };
      }) => ({
        id: message.id,
        subject: message.subject || "",
        from: message.from?.emailAddress
          ? `${message.from.emailAddress.name} <${message.from.emailAddress.address}>`
          : "",
        receivedDateTime: message.receivedDateTime,
        bodyText: message.body?.content || "",
      })
    );

  return messages.sort(
    (a, b) => new Date(a.receivedDateTime).getTime() - new Date(b.receivedDateTime).getTime()
  );
}

export interface MessageAttachmentMeta {
  name: string;
  contentType: string;
  contentBytes: string;
}

export async function getMessageAttachments(
  messageId: string,
  accessToken: string
): Promise<MessageAttachmentMeta[]> {
  // Pas de $select ici : Graph a un bug connu où filtrer les champs sur cette collection
  // polymorphe (Attachment de base vs fileAttachment/itemAttachment/referenceAttachment)
  // peut faire disparaître @odata.type et/ou contentBytes de la réponse, au lieu de
  // simplement les omettre proprement -- https://github.com/microsoftgraph/microsoft-graph-explorer-v4/issues/3916
  const url = `https://graph.microsoft.com/v1.0/me/messages/${encodeURIComponent(messageId)}/attachments`;

  const response = await fetchGraphWithRetry(url, accessToken);

  if (!response.ok) {
    throw new Error(`Graph request failed: ${response.status}`);
  }

  const data = await response.json();
  const raw: {
    "@odata.type"?: string;
    isInline?: boolean;
    name?: string;
    contentType?: string;
    contentBytes?: string;
  }[] = data.value || [];

  const attachments = raw
    .filter((attachment) => {
      const isFile = attachment["@odata.type"] === "#microsoft.graph.fileAttachment" || !!attachment.contentBytes;
      return isFile && !attachment.isInline;
    })
    .map(
      (attachment): MessageAttachmentMeta => ({
        name: attachment.name || "fichier",
        contentType: attachment.contentType || "application/octet-stream",
        contentBytes: attachment.contentBytes || "",
      })
    );

  console.log(
    `[getMessageAttachments] message ${messageId} : ${raw.length} pièce(s) jointe(s) au total, ${attachments.length} retenue(s) après filtre`
  );

  return attachments;
}
